// Prices per market. A market's currency follows its country (Barcelona →
// EUR); everything that shows or states a price goes through here instead
// of writing "$" / "USD".
import type { Tenant } from "@/lib/tenants";

// Eurozone members (ISO 3166-1 alpha-2).
const EURO = new Set([
  "AT", "BE", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV",
  "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES",
]);

export type Currency = "USD" | "EUR";

export const currencyForCountry = (country?: string | null): Currency =>
  country && EURO.has(country.toUpperCase()) ? "EUR" : "USD";

export const tenantCurrency = (t: Pick<Tenant, "country">): Currency => currencyForCountry(t.country);

export const currencySymbol = (c: Currency): string => (c === "EUR" ? "€" : "$");

/** "$1,500" / "€1,500" (amount with grouping, no decimals). */
export const formatMoney = (amount: number, c: Currency): string =>
  `${currencySymbol(c)}${Math.round(amount).toLocaleString("en-US")}`;

/** "$1,500" or "$1,500–$2,000" when a higher price applies to some stays. */
export const formatRent = (price: number, priceMax: number | null | undefined, c: Currency, varies = true): string =>
  varies && priceMax && priceMax > price
    ? `${formatMoney(price, c)}–${formatMoney(priceMax, c)}`
    : formatMoney(price, c);

/** A home the team listed without a price yet (Oct 30: admins may leave it empty) shows this instead of "$0". */
export const PRICE_ON_REQUEST = "Price on request";
export const hasPrice = (price: number | null | undefined): boolean => typeof price === "number" && price > 0;
